import { Box, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import { useLanguage } from '../../../hooks';
import { MAX_OFFSET_ALLOWED_HOURS, MIN_OFFSET_ALLOWED_HOURS } from './constants';
import { DateQuestionField, DateQuestionModeType, RangeConditionType } from './types';
import {
  convertDisplayOffsetValueToOffsetHours,
  convertOffsetHoursToDisplayOffsetValue,
} from './utils';

const customStyles: Record<string, SxProps<Theme>> = {
  rangeInputsRow: {
    display: 'flex',
    gap: '32px',
    alignItems: 'center',
  },
  simpleOffsetInputRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  rangeValueColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  rangeValueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  andColumn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  minimumOrMaximumValueTextField: {
    width: '70px',
    '& .MuiInputBase-input': {
      fontSize: '14px',
    },
  },
  simpleOffsetTextField: {
    width: '80px',
    '& .MuiInputBase-input': {
      fontSize: '14px',
    },
  },
  offsetLabel: {
    fontSize: '14px',
    mb: '8px',
  },
  andLabel: {
    fontSize: '14px',
  },
  offsetUnitTimeLabel: {
    fontSize: '14px',
  },
  validationErrorMessage: {
    color: 'error.main',
    fontSize: '12px',
    mt: '8px',
  },
};

interface CustomRangeConditionFieldsProps {
  selectedRangeCondition: RangeConditionType;
  dateMode: DateQuestionModeType;
  offsetHours?: string;
  offsetHoursToLeft?: string;
  offsetHoursToRight?: string;
  questionId: string;
  tableId?: string;
  onDateValidationChange: (
    questionId: string,
    dateQuestionFieldToUpdate: DateQuestionField,
    value: string,
    tableId?: string,
  ) => void;
}

export const CustomRangeConditionFields = (props: CustomRangeConditionFieldsProps) => {
  const {
    selectedRangeCondition,
    dateMode,
    offsetHours,
    offsetHoursToLeft,
    offsetHoursToRight,
    questionId,
    tableId,
    onDateValidationChange,
  } = props;

  const lang = useLanguage();

  if (!selectedRangeCondition) return <></>;

  const isPredefinedRangeCondition = ['onlyToday', 'noFutureDates', 'noPastDates'].includes(
    selectedRangeCondition,
  );
  if (isPredefinedRangeCondition) return <></>;

  const safeDateMode: DateQuestionModeType = dateMode || 'datetime';

  const getUnitTimeText = (value: number) => {
    const isSingular = value === 1;
    return safeDateMode === 'date'
      ? isSingular
        ? lang.form_builder.dateValidation.day
        : lang.form_builder.dateValidation.days
      : isSingular
      ? lang.form_builder.dateValidation.hour
      : lang.form_builder.dateValidation.hours;
  };

  const handleOffsetChange =
    (dateQuestionFieldToUpdate: DateQuestionField) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newOffsetValue = event.target.value;

      // This allows to delete the value of the field by pressing the delete key
      if (newOffsetValue === '') {
        onDateValidationChange(questionId, dateQuestionFieldToUpdate, '', tableId);
        return;
      }

      const isNonNegativeInteger = /^\d+$/.test(newOffsetValue);
      if (!isNonNegativeInteger) {
        return;
      }

      onDateValidationChange(
        questionId,
        dateQuestionFieldToUpdate,
        convertDisplayOffsetValueToOffsetHours(newOffsetValue, safeDateMode),
        tableId,
      );
    };

  const isBetweenTypeRangeCondition = ['between', 'notBetween'].includes(selectedRangeCondition);
  const minOffsetAllowedHours = MIN_OFFSET_ALLOWED_HOURS[safeDateMode];
  const maxOffsetAllowedHours = MAX_OFFSET_ALLOWED_HOURS[safeDateMode];
  const minOffsetAllowedValue = Number(
    convertOffsetHoursToDisplayOffsetValue(minOffsetAllowedHours.toString(), safeDateMode),
  );
  const maxOffsetAllowedValue =
    maxOffsetAllowedHours !== null
      ? Number(
          convertOffsetHoursToDisplayOffsetValue(maxOffsetAllowedHours.toString(), safeDateMode),
        )
      : null;

  if (isBetweenTypeRangeCondition) {
    const offsetToLeftValue = Number(
      convertOffsetHoursToDisplayOffsetValue(offsetHoursToLeft || '', safeDateMode),
    );
    const offsetToRightValue = Number(
      convertOffsetHoursToDisplayOffsetValue(offsetHoursToRight || '', safeDateMode),
    );

    const isInvalidOffsetToLeftValue =
      isNaN(offsetToLeftValue) ||
      offsetToLeftValue < minOffsetAllowedValue ||
      (maxOffsetAllowedValue !== null && offsetToLeftValue > maxOffsetAllowedValue);

    const isInvalidOffsetToRightValue =
      isNaN(offsetToRightValue) ||
      offsetToRightValue < minOffsetAllowedValue ||
      (maxOffsetAllowedValue !== null && offsetToRightValue > maxOffsetAllowedValue);

    const hasAnyOffsetValidationError = isInvalidOffsetToLeftValue || isInvalidOffsetToRightValue;

    const rangeValidationErrorMessage = hasAnyOffsetValidationError
      ? safeDateMode === 'date'
        ? lang.form_builder.dateValidation.rangeValidationErrorMessageForDate
        : safeDateMode === 'time'
        ? lang.form_builder.dateValidation.rangeValidationErrorMessageForTime
        : lang.form_builder.dateValidation.rangeValidationErrorMessageForDateTime
      : '';

    return (
      <>
        <Box sx={customStyles.rangeInputsRow}>
          <Box sx={customStyles.rangeValueColumn}>
            <Typography sx={customStyles.offsetLabel}>
              {lang.form_builder.dateValidation.minimumValue}
            </Typography>
            <Box sx={customStyles.rangeValueRow}>
              <TextField
                label=''
                value={convertOffsetHoursToDisplayOffsetValue(
                  offsetHoursToLeft || '',
                  safeDateMode,
                )}
                size='small'
                type='text'
                sx={customStyles.minimumOrMaximumValueTextField}
                onChange={handleOffsetChange('offsetHoursToLeft')}
                error={isInvalidOffsetToLeftValue}
                inputProps={{
                  inputMode: 'numeric',
                }}
              />
              <Typography sx={customStyles.offsetUnitTimeLabel}>
                {getUnitTimeText(offsetToLeftValue)}
              </Typography>
            </Box>
          </Box>

          <Box sx={customStyles.andColumn}>
            <Typography sx={customStyles.andLabel}>
              {lang.form_builder.dateValidation.and}
            </Typography>
          </Box>

          <Box sx={customStyles.rangeValueColumn}>
            <Typography sx={customStyles.offsetLabel}>
              {lang.form_builder.dateValidation.maximumValue}
            </Typography>
            <Box sx={customStyles.rangeValueRow}>
              <TextField
                label=''
                value={convertOffsetHoursToDisplayOffsetValue(
                  offsetHoursToRight || '',
                  safeDateMode,
                )}
                size='small'
                type='text'
                sx={customStyles.minimumOrMaximumValueTextField}
                onChange={handleOffsetChange('offsetHoursToRight')}
                error={isInvalidOffsetToRightValue}
                inputProps={{
                  inputMode: 'numeric',
                }}
              />
              <Typography sx={customStyles.offsetUnitTimeLabel}>
                {getUnitTimeText(offsetToRightValue)}
              </Typography>
            </Box>
          </Box>
        </Box>
        {rangeValidationErrorMessage && (
          <Typography sx={customStyles.validationErrorMessage}>
            {rangeValidationErrorMessage}
          </Typography>
        )}
      </>
    );
  }

  const offsetValue = Number(
    convertOffsetHoursToDisplayOffsetValue(offsetHours || '', safeDateMode),
  );

  const isInvalidOffsetValue =
    isNaN(offsetValue) ||
    offsetValue < minOffsetAllowedValue ||
    (maxOffsetAllowedValue !== null && offsetValue > maxOffsetAllowedValue);

  const validationErrorMessageInOffsetField =
    offsetValue < minOffsetAllowedValue
      ? `${
          lang.form_builder.dateValidation.minimumValueForOffsetIs
        } ${minOffsetAllowedValue} ${getUnitTimeText(minOffsetAllowedValue).toLowerCase()}`
      : maxOffsetAllowedValue !== null && offsetValue > maxOffsetAllowedValue
      ? `${
          lang.form_builder.dateValidation.maximumValueForOffsetIs
        } ${maxOffsetAllowedValue} ${getUnitTimeText(maxOffsetAllowedValue).toLowerCase()}`
      : '';

  return (
    <>
      <Typography sx={customStyles.offsetLabel}>
        {lang.form_builder.dateValidation.offset}
      </Typography>
      <Box sx={customStyles.simpleOffsetInputRow}>
        <TextField
          label=''
          value={convertOffsetHoursToDisplayOffsetValue(offsetHours || '', safeDateMode)}
          size='small'
          type='text'
          sx={customStyles.simpleOffsetTextField}
          onChange={handleOffsetChange('offsetHours')}
          error={isInvalidOffsetValue}
          inputProps={{
            inputMode: 'numeric',
          }}
        />
        <Typography sx={customStyles.offsetUnitTimeLabel}>
          {getUnitTimeText(offsetValue)}
        </Typography>
      </Box>
      {validationErrorMessageInOffsetField && (
        <Typography sx={customStyles.validationErrorMessage}>
          {validationErrorMessageInOffsetField}
        </Typography>
      )}
    </>
  );
};
